import { BLANK_TIPTAP_JSON, extractTextFromTipTap, MAX_NUM_CHARACTERS_PER_FIELD } from "./tipTapField";

export function storyBibleFieldTextLength(value) {
  if (value == null) return 0;
  if (typeof value === "string") {
    if (value.trim() === "") return 0;
    return value.replace(/<[^>]+>/g, "").length;
  }
  return extractTextFromTipTap(value).length;
}

export function isStoryBibleFieldOverLimit(value) {
  return storyBibleFieldTextLength(value) > MAX_NUM_CHARACTERS_PER_FIELD;
}

export function isPlainTextRoleOverLimit(value) {
  return typeof value === "string" && value.length > MAX_NUM_CHARACTERS_PER_FIELD;
}

export function formatStoryBibleFieldLimit() {
  return MAX_NUM_CHARACTERS_PER_FIELD.toLocaleString("en-US");
}

export function storyBibleFieldsNotSavedToastMessage() {
  return `Some fields weren't saved because they exceed the ${formatStoryBibleFieldLimit()}-character limit. Shorten the highlighted fields and try again.`;
}

const CHARACTER_TIPTAP_FIELD_KEYS = ["name", "pronouns", "groups", "otherNames"];
const WORLDBUILDING_TIPTAP_FIELD_KEYS = ["name", "otherNames"];

function addCustomFieldOversizedKeys(customFields, keys) {
  if (!Array.isArray(customFields)) return;
  for (const field of customFields) {
    if (isStoryBibleFieldOverLimit(field?.value)) {
      keys.add(`customField:${field.id}`);
    }
  }
}

export function getCharacterOversizedFieldKeys(character) {
  const keys = new Set();
  if (!character) return keys;

  for (const key of CHARACTER_TIPTAP_FIELD_KEYS) {
    if (isStoryBibleFieldOverLimit(character[key])) {
      keys.add(key);
    }
  }
  if (isPlainTextRoleOverLimit(character.roles)) {
    keys.add("roles");
  }
  addCustomFieldOversizedKeys(character.customFields, keys);
  return keys;
}

export function getWorldbuildingOversizedFieldKeys(entry) {
  const keys = new Set();
  if (!entry) return keys;

  for (const key of WORLDBUILDING_TIPTAP_FIELD_KEYS) {
    if (isStoryBibleFieldOverLimit(entry[key])) {
      keys.add(key);
    }
  }
  if (isPlainTextRoleOverLimit(entry.role)) {
    keys.add("role");
  }
  addCustomFieldOversizedKeys(entry.customFields, keys);
  return keys;
}

export function characterHasOversizedFields(character) {
  return getCharacterOversizedFieldKeys(character).size > 0;
}

export function worldbuildingEntryHasOversizedFields(entry) {
  return getWorldbuildingOversizedFieldKeys(entry).size > 0;
}

function pickCharacterFieldValue(key, characterToSave, savedCharacter, oversizedKeys, skippedFields) {
  if (!oversizedKeys.has(key)) {
    return characterToSave[key];
  }
  skippedFields.push(key);
  return savedCharacter?.[key] ?? characterToSave[key];
}

function pickPlainRoleValue(localRole, savedRole, roleKey, oversizedKeys, skippedFields) {
  if (!oversizedKeys.has(roleKey)) {
    return localRole;
  }
  skippedFields.push(roleKey);
  return savedRole ?? localRole;
}

function mergeCustomFieldsForSave(localFields, savedFields, oversizedKeys, skippedFields) {
  const savedById = new Map((savedFields || []).map((field) => [field.id, field]));

  return (localFields || []).map((localField) => {
    const key = `customField:${localField.id}`;
    if (!oversizedKeys.has(key)) {
      return localField;
    }

    skippedFields.push(localField.label || key);
    const savedField = savedById.get(localField.id);
    if (savedField) {
      return { ...savedField };
    }
    return { ...localField, value: BLANK_TIPTAP_JSON };
  });
}

export function buildCharacterSavePayload(characterToSave, savedCharacter) {
  const oversizedKeys = getCharacterOversizedFieldKeys(characterToSave);
  const skippedFields = [];

  const character = {
    ...characterToSave,
    name: pickCharacterFieldValue("name", characterToSave, savedCharacter, oversizedKeys, skippedFields),
    roles: pickPlainRoleValue(characterToSave.roles, savedCharacter?.roles, "roles", oversizedKeys, skippedFields),
    pronouns: pickCharacterFieldValue("pronouns", characterToSave, savedCharacter, oversizedKeys, skippedFields),
    groups: pickCharacterFieldValue("groups", characterToSave, savedCharacter, oversizedKeys, skippedFields),
    otherNames: pickCharacterFieldValue("otherNames", characterToSave, savedCharacter, oversizedKeys, skippedFields),
    customFields: mergeCustomFieldsForSave(
      characterToSave.customFields,
      savedCharacter?.customFields,
      oversizedKeys,
      skippedFields,
    ),
  };

  return { character, skippedFields };
}

export function buildWorldbuildingSavePayload(entryToSave, savedEntry) {
  const oversizedKeys = getWorldbuildingOversizedFieldKeys(entryToSave);
  const skippedFields = [];

  const entry = {
    ...entryToSave,
    name: pickCharacterFieldValue("name", entryToSave, savedEntry, oversizedKeys, skippedFields),
    role: pickPlainRoleValue(entryToSave.role, savedEntry?.role, "role", oversizedKeys, skippedFields),
    otherNames: pickCharacterFieldValue("otherNames", entryToSave, savedEntry, oversizedKeys, skippedFields),
    customFields: mergeCustomFieldsForSave(
      entryToSave.customFields,
      savedEntry?.customFields,
      oversizedKeys,
      skippedFields,
    ),
  };

  return { entry, skippedFields };
}

function restoreCharacterTipTapField(key, merged, localCharacter, serverCharacter) {
  if (isStoryBibleFieldOverLimit(localCharacter[key])) {
    merged[key] = localCharacter[key];
  } else if (serverCharacter?.[key] !== undefined) {
    merged[key] = serverCharacter[key];
  }
}

export function mergeCharacterPreservingOversizedFields(serverCharacter, localCharacter) {
  if (!characterHasOversizedFields(localCharacter)) {
    return serverCharacter;
  }

  const merged = { ...serverCharacter };
  for (const key of CHARACTER_TIPTAP_FIELD_KEYS) {
    restoreCharacterTipTapField(key, merged, localCharacter, serverCharacter);
  }
  if (isPlainTextRoleOverLimit(localCharacter.roles)) {
    merged.roles = localCharacter.roles;
  }

  const serverFieldsById = new Map((serverCharacter.customFields || []).map((field) => [field.id, field]));
  merged.customFields = (localCharacter.customFields || []).map((localField) => {
    if (isStoryBibleFieldOverLimit(localField.value)) {
      return localField;
    }
    return serverFieldsById.get(localField.id) ?? localField;
  });

  return merged;
}

export function mergeWorldbuildingPreservingOversizedFields(serverEntry, localEntry) {
  if (!worldbuildingEntryHasOversizedFields(localEntry)) {
    return serverEntry;
  }

  const merged = { ...serverEntry };
  for (const key of WORLDBUILDING_TIPTAP_FIELD_KEYS) {
    restoreCharacterTipTapField(key, merged, localEntry, serverEntry);
  }
  if (isPlainTextRoleOverLimit(localEntry.role)) {
    merged.role = localEntry.role;
  }

  const serverFieldsById = new Map((serverEntry.customFields || []).map((field) => [field.id, field]));
  merged.customFields = (localEntry.customFields || []).map((localField) => {
    if (isStoryBibleFieldOverLimit(localField.value)) {
      return localField;
    }
    return serverFieldsById.get(localField.id) ?? localField;
  });

  return merged;
}
